export function humanizeSlug(input: string | null | undefined): string {
  if (!input) return ''
  return String(input)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function slugify(input: string | null | undefined): string {
  if (!input) return ''
  return String(input)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function labelize(input: string | null | undefined): string {
  if (!input) return ''
  const withSpaces = String(input)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
  return withSpaces
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * Coerce a list-item value into displayable text.
 *
 * Repeater fields that are meant to be plain strings can end up holding objects when content is generated
 * programmatically — commonly `{ feature: "..." }`, `{ text: "..." }`, or
 * `{ label: "..." }`. Rendering such an object directly throws React error
 * #31 ("Objects are not valid as a React child"), which aborts SSR and blanks
 * the page. This normalizes any of those shapes back to a string so the
 * render is resilient regardless of how the data was stored.
 */
export function toDisplayText(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    const candidate = obj.feature ?? obj.text ?? obj.label ?? obj.title ?? obj.name ?? obj.value
    if (typeof candidate === 'string') return candidate
    if (typeof candidate === 'number' || typeof candidate === 'boolean') return String(candidate)
  }
  return ''
}

export function formatModuleGroupName(name: string, isDefault?: boolean): string {
  if (!name) return ''
  let cleanName = name
  let isDef = !!isDefault

  if (cleanName.toLowerCase().endsWith('-default') || cleanName.toLowerCase().endsWith('_default')) {
    cleanName = cleanName.slice(0, -8)
    isDef = true
  } else if (cleanName.toLowerCase().endsWith(' default')) {
    cleanName = cleanName.slice(0, -8)
    isDef = true
  }

  const base = labelize(cleanName)
  return isDef ? `${base} (Default)` : base
}

/**
 * Generate unique anchor IDs for a list of modules.
 * If multiple modules have the same slugified label/type, appends -2, -3 etc.
 */
export function getModuleAnchors(
  modules: Array<{ id: string; type: string; adminLabel?: string | null }>
): Map<string, string> {
  const seenSlugs = new Map<string, number>()
  const anchors = new Map<string, string>()

  for (const m of modules) {
    const baseSlug = slugify(m.adminLabel || m.type)
    const count = seenSlugs.get(baseSlug) || 0
    const finalSlug = count === 0 ? baseSlug : `${baseSlug}-${count + 1}`

    anchors.set(m.id, `#${finalSlug}`)
    seenSlugs.set(baseSlug, count + 1)
  }

  return anchors
}
